import { useCallback, useContext, useEffect, useReducer, useState } from "react";
import { useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

import ActionButton from "../components/formInputs/buttons/ActionButton";
import BackOnly from "../components/saveBlock/BackOnly";
import BreadCrumb from "../components/breadCrumb/BreadCrumb";
import DcsdDialog from "../components/modals/DcsdDialog";
import FeederBlock from "../components/feeders/FeederBlock";
import FormReducer from "../utils/FormReducer";
import GoldenRodBox from "../components/goldenRodBox/GoldenRodBox";
import Header from "../components/header/Header";
import LoadingSvg from "../components/loadingSvg/LoadingSvg";
import OpenEnrollmentDao from "../dao/OpenEnrollmentDao";
import RbA from "../components/rba/RbA";
import SaveAcceptedOffer from "../components/saveBlock/SaveAcceptedOffer";
import SaveSchoolChoices from "../components/saveBlock/SaveSchoolChoices";
import SelectedSchools from "../components/selectedSchools/SelectedSchools";
import SelectedSchoolsBlock from "../components/selectedSchools/SelectedSchoolsBlock";
import StudentInfoBlock from "../components/studentInfoBlock/StudentInfoBlock";
import StudentInfoDao from "../dao/StudentInfoDao";
import T from "../components/translations/Translations";
import Toolbar from "../components/Toolbar";
import WindowAndPermissionDialog from "../components/modals/WindowAndPermissionDialog";

import { EMPLOYEE_DASHBOARD, GUARDIAN_DASHBOARD } from "../utils/auth/config";
import { formatDateAndTime, getTodayWithTime } from "../utils/DateFormatter";
import { GlobalContext } from "../components/contextProvider/ContextProvider";

import "../styles/SchoolChoice.scss";

/**
 * @constructor
 * @description School Choice selection and disposition page
 * @name SchoolChoice
 * @returns {JSX.Element}
 */
const SchoolChoice = () => {
    const { key } = useParams();
    const { dispatch, state } = useContext(GlobalContext);
    const { adwCalled, icStudentInfo, oeActiveDistrictWindow, oeStudentInfo, oeWindows, token, userDetails } =
        state || {};

    const initialFormState = { employeeNumber: "", employeeUsername: "", schoolChoiceDetailDtos: [] };
    const [formState, formDispatch] = useReducer(FormReducer, initialFormState);

    const [employeeDelete, setEmployeeDelete] = useState(false);
    const [feeders, setFeeders] = useState(null);
    const [feederFlag, setFeederFlag] = useState(true);
    const [guardianInfo, setGuardianInfo] = useState(null);
    const [isMutable, setIsMutable] = useState(false);
    const [loader, setLoader] = useState(false);
    const [nextParentOffer, setNextParentOffer] = useState(null);
    const [nextSteps, setNextSteps] = useState(null);
    const [oeGuardian, setOeGuardian] = useState(null);
    const [oeGuardianFlag, setOeGuardianFlag] = useState(true);
    const [oeStudentInfoFlag, setOeStudentInfoFlag] = useState(true);
    const [open, setOpen] = useState("false");
    const [outOfBounds, setOutOfBounds] = useState(false);
    const [parentOffer, setParentOffer] = useState(null);
    const [parentRequest, setParentRequest] = useState(null);
    const [reasons, setReasons] = useState(null);
    const [schoolFinalize, setSchoolFinalize] = useState(null);
    const [selectedSchoolTab, setSelectedSchoolTab] = useState(0);
    const [sorryMessage, setSorryMessage] = useState("");
    const [sorryTitle, setSorryTitle] = useState("");

    const allowedRolesArray = ["EXTERNAL_GUARDIAN", "GUARDIAN"];

    const oeButtonsCancel = T({ key: "OE_Buttons_Cancel" });
    const oeButtonsDelete = T({ key: "OE_Buttons_Delete" });
    const oeDialogDeleteTitle = T({ key: "OE_Dialog_Delete_Title" });
    const oeDialogDeleteBody1 = T({ key: "OE_Dialog_Delete_Body1" });
    const oeDialogDeleteBody2 = T({ key: "OE_Dialog_Delete_Body2" });
    const breadcrumbSchoolSelection = T({ key: "Breadcrumb_School_Selection" });
    const breadcrumbSchoolsMakingOffers = T({ key: "Breadcrumb_Schools_Making_Offers" });

    const nextSteps0 = T({ key: "OE_Next_Steps" });
    const nextSteps1 = T({ key: "OE_Next_Steps_1" });
    const nextSteps2 = T({ key: "OE_Next_Steps_2" });
    const nextStepsPost = T({ key: "OE_Next_Steps_Post" });
    const importantParentInfo = T({ key: "OE_Important_Parent_Info" });

    const toastDataError = T({ key: "OE_Toast_Data_Error" });
    const toastDeleted = T({ key: "OE_Toast_Deleted" });
    const toastDeleteError = T({ key: "OE_Toast_Delete_Error" });
    const toastIcError = T({ key: "OE_Toast_IC_Error" });
    const toastRegionError = T({ key: "OE_Toast_Region_Error" });
    const toastReasonsError = T({ key: "OE_Toast_Reasons_Error" });
    const toastRedirect = T({ key: "OE_Toast_Redirecting" });
    const toastSchoolChoiceError = T({ key: "OE_Toast_Update_Error" });
    const toastSchoolChoiceSuccess = T({ key: "OE_Toast_Saved" });
    const toastStudentError = T({ key: "OE_Toast_Student_Error" });
    const toastChoiceSuccess = T({ key: "OE_Toast_School_Choice_Success" });

    const toastEmailError = T({ key: "OE_Toast_Email_Error" });
    const toastEmailSuccess = T({ key: "OE_Toast_Email" });
    const toastEmailFailure = T({ key: "OE_Toast_Email_Failure" });

    const dialogClosedTitle = T({ key: "OE_Dialog_Window_Closed_Title" });
    const dialogClosedBody = T({ key: "OE_Dialog_Window_Closed_Body" });
    const dialogAccessDeniedTitle = T({ key: "OE_Dialog_Access_Denied_Title" });
    const dialogAccessDeniedBody = T({ key: "OE_Dialog_Access_Denied_Body" });
    const dialogProcessing = T({ key: "OE_Dialog_Processing" });

    const emailGuardian = useCallback(() => {
        if (guardianInfo && key && oeActiveDistrictWindow && token && guardianInfo.email?.length > 0) {
            const options = {
                action: "oeGuardianEmail",
                key,
                schoolYearKey: oeActiveDistrictWindow.schoolYearKey,
                token
            };
            OpenEnrollmentDao(options)
                .then((response) => {
                    if (response) {
                        const { errors } = response.data;
                        if (errors && errors.length > 0) {
                            let errorMessage = toastEmailError;
                            errorMessage = `${errorMessage}:\n${errors.join("\n")}`;
                            toast.error(`${errorMessage}`, {
                                autoClose: false,
                                closeOnClick: true,
                                style: { width: "100%", whiteSpace: "break-spaces" }
                            });
                        } else {
                            toast.success(`${toastEmailSuccess} ${guardianInfo.email}.`, {
                                autoClose: false
                            });
                        }
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setTimeout(() => {
                        sessionStorage.clear();
                        window.location.replace(GUARDIAN_DASHBOARD);
                    }, 4000);
                });
        } else {
            toast.info(toastEmailFailure, {
                autoClose: false
            });
            setTimeout(() => {
                sessionStorage.clear();
                window.location.replace(GUARDIAN_DASHBOARD);
            }, 4000);
        }
    }, [guardianInfo, key, oeActiveDistrictWindow, toastEmailError, toastEmailFailure, toastEmailSuccess, token]);

    const getBreadCrumbLabel = () => {
        let label = "Open Enrollment";
        if (nextParentOffer || parentOffer) {
            label = `${label} > ${breadcrumbSchoolsMakingOffers}`;
        } else {
            label = `${label} > ${breadcrumbSchoolSelection}`;
        }

        return label;
    };

    const getEmployeeIdTitle = useCallback(() => {
        return employeeDelete ? "Delete DCSD Employee Priority Points" : "DCSD Employee Priority Points";
    }, [employeeDelete]);

    const getEnrollmentRound = (er) => {
        if (er === "ROUND1") {
            return "Round 1";
        } else if (er === "ROUND2") {
            return "Round 2";
        }

        return er;
    };

    const getDeleteActions = () => {
        return (
            <>
                <ActionButton
                    className="action-button-cancel"
                    label={oeButtonsCancel}
                    onClick={() => {
                        setOpen("false");
                    }}
                />
                <ActionButton
                    className="action-button-delete"
                    disable={loader}
                    label={oeButtonsDelete}
                    onClick={() => {
                        handleDelete();
                    }}
                />
            </>
        );
    };

    const getEmployeeIdActions = useCallback(() => {
        return employeeDelete ? (
            <>
                {oeGuardian && (
                    <ActionButton
                        className="action-button-delete-100"
                        disable={loader}
                        label="Delete"
                        onClick={handleEmployeeIdDelete}
                    />
                )}
                <ActionButton
                    className="action-button-cancel"
                    label={oeButtonsCancel}
                    onClick={() => {
                        toast.dismiss();
                        setEmployeeDelete(false);
                        setOpen("false");
                    }}
                />
            </>
        ) : (
            <>
                {oeGuardian && (
                    <ActionButton
                        className="action-button-delete-100"
                        label="Delete"
                        onClick={() => {
                            setEmployeeDelete(true);
                        }}
                    />
                )}
                <ActionButton
                    className="action-button-cancel"
                    label={oeButtonsCancel}
                    onClick={() => {
                        toast.dismiss();
                        setEmployeeDelete(false);
                        setOpen("false");
                    }}
                />
                <ActionButton
                    className="action-button-150"
                    disable={loader}
                    label="Save"
                    onClick={handleEmployeeIdSubmit}
                />
            </>
        );
        /* eslint-disable-next-line */
    }, [
        employeeDelete,
        formState,
        guardianInfo,
        handleEmployeeIdSubmit,
        handleEmployeeIdDelete,
        loader,
        oeGuardian,
        token,
        userDetails
    ]);

    const getParentMessage = useCallback(() => {
        if (parentOffer && formState.schoolChoiceDetailDtos?.length > 0) {
            return (
                <p>
                    The Open Enrollment window for {getEnrollmentRound(oeActiveDistrictWindow.enrollmentRound)} is now
                    open to review offers from your schools of interest. Please note, you may accept only one school
                    offer by clicking the &ldquo;Accept&rdquo; button. Once you&apos;ve accepted one school&apos;s
                    invitation, all other offers (as applicable) will be declined automatically. You can also manually
                    decline any or all offers extended by schools during this window. Once you have completed your
                    review, please click the &ldquo;Submit&rdquo; button to save your selections. You must disposition
                    offers by <b>{formatDateAndTime(parentOffer.endDate)}</b>.
                </p>
            );
        }

        return (
            <p>
                The Open Enrollment request window for {getEnrollmentRound(oeActiveDistrictWindow.enrollmentRound)} is
                now closed as schools review all parent requests. Please return beginning{" "}
                <b>{formatDateAndTime(nextParentOffer?.startDate) || ""}</b> to review offers and plan to take any
                required actions by <b>{formatDateAndTime(nextParentOffer?.endDate) || ""}</b> when the parent offer
                window closes.
            </p>
        );
    }, [formState.schoolChoiceDetailDtos, nextParentOffer, oeActiveDistrictWindow, parentOffer]);

    const getSchoolChoiceDto = () => {
        const { schoolChoiceDetailDtos } = structuredClone(formState);
        const studentDetailsDto = structuredClone(oeStudentInfo);
        let cnt = 1;
        const newSchoolChoiceDetailDtos = schoolChoiceDetailDtos.reduce((results, dto) => {
            const tmpDto = dto;
            tmpDto.priority = cnt++;
            tmpDto.completionDate = dto.completionDate ? dto.completionDate : getTodayWithTime();
            if (tmpDto.schoolDto) {
                delete tmpDto.schoolDto;
            }
            const { kinderChoice, languageChoice } = dto;
            if (kinderChoice && kinderChoice.length > 0) {
                tmpDto.kinderChoice = kinderChoice.join(",");
            }
            if (languageChoice && languageChoice.length > 0) {
                tmpDto.languageChoice = languageChoice.join(",");
            }
            results.push(tmpDto);

            return results;
        }, []);

        studentDetailsDto.schoolChoiceDetailDtos = newSchoolChoiceDetailDtos;

        return studentDetailsDto;
    };

    const handleChangeEmployee = (e) => {
        const { name, value } = e.target;
        formDispatch({
            type: "text",
            field: name,
            payload: value.trim()
        });
    };

    const handleDelete = () => {
        const options = {
            action: "oeByStudentKeyDelete",
            key,
            token
        };
        setLoader(true);
        OpenEnrollmentDao(options)
            .then((response) => {
                if (response) {
                    const { errors } = response.data;
                    if (errors && errors?.length > 0) {
                        let errorMessage = toastDeleteError;
                        errorMessage = `${errorMessage}:\n${errors.join("\n")}`;
                        toast.error(`${errorMessage}`, {
                            autoClose: false,
                            closeOnClick: true,
                            style: { width: "100%", whiteSpace: "break-spaces" }
                        });
                        setOpen("false");

                        return false;
                    }
                    toast.success(toastDeleted, {
                        autoClose: false
                    });
                    setOpen("false");
                    setTimeout(() => {
                        sessionStorage.clear();
                        window.location.replace(GUARDIAN_DASHBOARD);
                    }, 4000);

                    return true;
                }
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                setLoader(false);
            });
    };

    const validateForm = useCallback(() => {
        const { employeeNumber, employeeUsername } = formState;
        if (/\s/.test(employeeNumber) || /\s/.test(employeeUsername)) {
            toast.error("Username and Employee ID fields cannot contain spaces!", { autoClose: false });
            return false;
        }
        if (employeeNumber && employeeNumber.charAt(0) === "0") {
            toast.error("Employee ID cannot start with a '0'!", { autoClose: false });
            return false;
        }
        if (!/^\d+$/.test(employeeNumber)) {
            toast.error("Employee ID must be a number!", { autoClose: false });
            return false;
        }

        return true;
    }, [formState]);

    const handleEmployeeIdDelete = useCallback(() => {
        toast.dismiss();
        const options = {
            action: "oeGuardianDelete",
            key: oeGuardian.key,
            token
        };
        setLoader(true);
        OpenEnrollmentDao(options)
            .then((response) => {
                if (response) {
                    const { errors } = response.data;
                    if (errors && errors.length > 0) {
                        let errorMessage = "There was a problem linking your employee data.";
                        errorMessage = `${errorMessage}:\n${errors.join("\n")}`;

                        toast.error(`${errorMessage}`, {
                            autoClose: false,
                            closeOnClick: true,
                            style: { width: "100%", whiteSpace: "break-spaces" }
                        });
                    } else {
                        setOeGuardian(null);
                        const tmpFormState = structuredClone(formState);
                        tmpFormState.employeeNumber = "";
                        tmpFormState.employeeUsername = "";
                        formDispatch({
                            type: "reset",
                            payload: { ...tmpFormState }
                        });
                        toast.success(
                            "The DCSD Employee Username and DCSD Employee ID associated with this Open Enrollment request has been successfully removed."
                        );
                    }
                    setEmployeeDelete(false);
                    window.location.reload();
                }
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                setLoader(false);
            });
    }, [formDispatch, formState, oeGuardian, token]);

    const handleEmployeeIdSubmit = useCallback(() => {
        const employeeIdForm = document.getElementById("employee-id-form");
        const checkStatus = employeeIdForm.checkValidity();
        employeeIdForm.reportValidity();
        if (checkStatus && validateForm()) {
            toast.dismiss();
            setLoader(true);
            const action = oeGuardian ? "oeGuardianUpdate" : "oeGuardianCreate";
            const data = {
                employeeNumber: parseInt(formState.employeeNumber, 10),
                employeeUsername: formState.employeeUsername,
                key: oeGuardian ? oeGuardian.key : null,
                parentGuid: parseInt(userDetails.uid, 10)
            };
            const successVerb = oeGuardian ? "updated" : "added";
            const options = {
                action,
                data,
                key: oeGuardian ? oeGuardian.key : null,
                token
            };
            OpenEnrollmentDao(options)
                .then((response) => {
                    if (response) {
                        const { errors, payload } = response.data;
                        if (payload) {
                            setOeGuardian(payload);
                            toast.success(
                                `The DCSD Employee Username and DCSD Employee ID associated with this Open Enrollment request has been successfully ${successVerb}`
                            );
                        } else if (errors) {
                            let errorMessage = "There was a problem linking your employee data.";
                            if (errors && errors.length > 0) {
                                errorMessage = `${errorMessage}:\n${errors.join("\n")}`;
                            }
                            toast.error(`${errorMessage}`, {
                                autoClose: false,
                                closeOnClick: true,
                                style: { width: "100%", whiteSpace: "break-spaces" }
                            });
                        }
                        window.location.reload();
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    }, [formState, oeGuardian, token, userDetails, validateForm]);
    /**
     * @description handle school choice selections, accept offers, deny offers
     * @callback
     * @name handleSubmit
     * @param {boolean} autoSave
     */
    const handleSubmit = (autoSave) => {
        const data = getSchoolChoiceDto();
        const options = {
            action: "oeByStudentKeyUpdate",
            data,
            key: oeStudentInfo.studentDto.key,
            token
        };
        setLoader(true);
        OpenEnrollmentDao(options)
            .then((response) => {
                if (response) {
                    const { errors, payload } = response.data;
                    if (payload) {
                        // don't send an email if a parent is Accepting or Denying a school offer
                        if (parentOffer) {
                            const tMessage = autoSave ? toastChoiceSuccess : `${toastChoiceSuccess} ${toastRedirect}`;
                            toast.success(tMessage, {
                                autoClose: 3000
                            });
                            if (!autoSave) {
                                setTimeout(() => {
                                    sessionStorage.clear();
                                    window.location.replace(GUARDIAN_DASHBOARD);
                                }, 4000);
                            } else {
                                setLoader(false);
                            }
                        } else {
                            // Parent Request
                            toast.success(toastSchoolChoiceSuccess, {
                                autoClose: false
                            });
                            emailGuardian(true);
                        }
                    } else if (errors) {
                        let errorMessage = toastSchoolChoiceError;
                        if (errors && errors.length > 0) {
                            errorMessage = `${errorMessage}:\n${errors.join("\n")}`;
                        }
                        toast.error(`${errorMessage}`, {
                            autoClose: false,
                            closeOnClick: true,
                            style: { width: "100%", whiteSpace: "break-spaces" }
                        });
                        setLoader(false);
                    }
                }
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const showNotice = () => {
        return (
            !outOfBounds && feeders && formState && oeStudentInfo && (nextParentOffer || parentOffer || schoolFinalize)
        );
    };

    const updateSchoolChoiceDetailDtos = useCallback(
        (feedersObj) => {
            const feedersKeys = Object.keys(feedersObj);
            const tmpFormState = structuredClone(formState);
            const { schoolChoiceDetailDtos } = tmpFormState;

            if (feedersObj && schoolChoiceDetailDtos && schoolChoiceDetailDtos.length > 0) {
                const newDtos = schoolChoiceDetailDtos.reduce((results, dto) => {
                    const tmpDto = structuredClone(dto);
                    const { kinderChoice, languageChoice } = tmpDto;

                    const schoolMatch = feedersKeys.reduce((results2, feeder) => {
                        let match = [];
                        match = feedersObj[feeder].filter(
                            (obj) => parseInt(obj.key, 10) === parseInt(dto.participatingSchoolId, 10)
                        );
                        if (match && match.length > 0) {
                            results2.push(match[0]);
                        }
                        return results2;
                    }, []);
                    tmpDto.kinderChoice =
                        kinderChoice && kinderChoice.length > 0 ? kinderChoice.split(",") : kinderChoice;
                    tmpDto.languageChoice =
                        languageChoice && languageChoice.length > 0 ? languageChoice.split(",") : languageChoice;
                    tmpDto.schoolDto = schoolMatch[0];
                    results.push(tmpDto);

                    return results;
                }, []);

                tmpFormState.schoolChoiceDetailDtos = newDtos;
                formDispatch({
                    type: "reset",
                    payload: { ...tmpFormState }
                });
            }
        },
        [formState]
    );

    const showEmployeeButton = () => {
        return oeActiveDistrictWindow && oeActiveDistrictWindow.enrollmentRound === "ROUND1" && parentRequest;
    };

    /**
     * Get reasons
     */
    useEffect(() => {
        if (token && !reasons) {
            const options = {
                action: "oeGetReasons",
                token
            };
            setLoader(true);
            OpenEnrollmentDao(options)
                .then((response) => {
                    if (response) {
                        const { errors, payload } = response.data;
                        if (payload && payload.length > 0) {
                            payload.sort((a, b) => {
                                return a.questionText > b.questionText ? 1 : -1;
                            });
                            const other = payload.filter((obj) => obj.questionText.toLowerCase() === "other");
                            if (other && other.length > 0) {
                                let match = [];
                                match = payload.filter((obj) => obj.questionText.toLowerCase() !== "other");
                                match.push(other[0]);
                                setReasons(match);
                            }
                        } else if (errors) {
                            let errorMessage = toastReasonsError;
                            if (errors && errors.length > 0) {
                                errorMessage = `${errorMessage}:\n${errors.join("\n")}`;
                            }
                            toast.error(`${errorMessage}`, {
                                autoClose: false,
                                closeOnClick: true,
                                style: { width: "100%", whiteSpace: "break-spaces" }
                            });
                        }
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    }, [reasons, toastReasonsError, token]);

    /**
     * If not oeActiveDistrictWindow, shut it down
     */
    useEffect(() => {
        if (adwCalled && !oeActiveDistrictWindow) {
            setOutOfBounds(true);
            setSorryTitle(dialogClosedTitle);
            setSorryMessage(dialogClosedBody);
            setOpen("out-of-bounds");
        }
    }, [adwCalled, dialogClosedBody, dialogClosedTitle, oeActiveDistrictWindow]);

    /**
     * Get IC guardian information (infinite campus)
     */
    useEffect(() => {
        if (token && userDetails && !guardianInfo) {
            const { roleDtos } = userDetails;
            const match = roleDtos.filter((obj) => obj.role === "GUARDIAN");
            if (match && match.length > 0) {
                const options = {
                    action: "guardianInfoRead",
                    username: userDetails?.username,
                    token
                };
                setLoader(true);
                StudentInfoDao(options)
                    .then((response) => {
                        if (response) {
                            const { errors, payload } = response.data;
                            if (payload) {
                                setGuardianInfo(payload);
                            } else if (errors) {
                                setGuardianInfo(null);
                                let errorMessage = toastDataError;
                                if (errors && errors.length > 0) {
                                    errorMessage = `${errorMessage}:\n${errors.join("\n")}`;
                                }
                                toast.error(`${errorMessage}`, {
                                    autoClose: false,
                                    closeOnClick: true,
                                    style: { width: "100%", whiteSpace: "break-spaces" }
                                });
                            }
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                    })
                    .finally(() => {
                        setLoader(false);
                    });
            } else {
                setGuardianInfo(userDetails);
            }
        }
    }, [guardianInfo, toastDataError, token, userDetails]);

    /**
     * Get OE Student Data
     */
    useEffect(() => {
        if (key && token && oeActiveDistrictWindow && oeStudentInfoFlag && !oeStudentInfo) {
            setOeStudentInfoFlag(false);
            const options = {
                action: "oeByStudentKeyRead",
                key: key,
                token
            };
            setLoader(true);
            OpenEnrollmentDao(options)
                .then((response) => {
                    if (response) {
                        const { errors, payload } = response.data;
                        if (payload) {
                            const tmpFormState = structuredClone(formState);
                            payload.schoolChoiceDetailDtos?.sort((a, b) => {
                                return parseInt(a.priority, 10) > parseInt(b.priority, 10) ? 1 : -1;
                            });
                            tmpFormState.schoolChoiceDetailDtos = payload.schoolChoiceDetailDtos;
                            dispatch({
                                type: "OeStudentInfo",
                                oeStudentInfo: payload
                            });
                            formDispatch({
                                type: "reset",
                                payload: { ...tmpFormState }
                            });
                        } else if (errors) {
                            let errorMessage = toastStudentError;
                            if (errors && errors.length > 0) {
                                errorMessage = `${errorMessage}:\n${errors.join("\n")}`;
                            }
                            toast.error(`${errorMessage}`, {
                                autoClose: false,
                                closeOnClick: true,
                                style: { width: "100%", whiteSpace: "break-spaces" }
                            });
                        }
                    }
                })
                .catch((error) => {
                    console.error(error);
                    setOutOfBounds(true);
                    setSorryTitle(dialogAccessDeniedTitle);
                    setSorryMessage(dialogAccessDeniedBody);
                    setOpen("out-of-bounds");
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    }, [
        dialogAccessDeniedBody,
        dialogAccessDeniedTitle,
        dispatch,
        formDispatch,
        formState,
        key,
        oeActiveDistrictWindow,
        oeStudentInfo,
        oeStudentInfoFlag,
        toastStudentError,
        token
    ]);

    useEffect(() => {
        if (token && userDetails && oeGuardianFlag && !oeStudentInfoFlag && !oeGuardian) {
            setOeGuardianFlag(false);
            const options = {
                action: "oeGuardianByGuidRead",
                guid: userDetails.uid,
                token
            };
            setLoader(true);
            OpenEnrollmentDao(options)
                .then((response) => {
                    if (response) {
                        const { payload } = response.data;
                        if (payload) {
                            setOeGuardian(payload);
                        }
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
        /* eslint-disable-next-line */
    }, [formDispatch, formState, oeGuardian, oeGuardianFlag, oeStudentInfoFlag, token, userDetails]);

    /**
     * Get OE Student Data
     */
    useEffect(() => {
        if (oeStudentInfo && token && !icStudentInfo) {
            const { studentDto } = oeStudentInfo;
            if (studentDto?.studentNumber) {
                const options = {
                    action: "studentInfoByNumberGet",
                    studentNumber: studentDto.studentNumber,
                    token
                };
                setLoader(true);
                StudentInfoDao(options)
                    .then((response) => {
                        if (response) {
                            const { errors, payload } = response.data;
                            if (payload) {
                                dispatch({
                                    type: "IcStudentInfo",
                                    icStudentInfo: payload
                                });
                            } else if (errors && errors.length > 0) {
                                let errorMessage = toastIcError;
                                if (errors && errors.length > 0) {
                                    errorMessage = `${errorMessage}:\n${errors.join("\n")}`;
                                }
                                toast.error(`${errorMessage}`, {
                                    autoClose: false,
                                    closeOnClick: true,
                                    style: { width: "100%", whiteSpace: "break-spaces" }
                                });
                            }
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                    })
                    .finally(() => {
                        setLoader(false);
                    });
            }
        }
    }, [dispatch, icStudentInfo, oeStudentInfo, toastIcError, token]);

    useEffect(() => {
        if (feederFlag && oeActiveDistrictWindow && oeStudentInfo && token && !feeders) {
            setFeederFlag(false);
            const { studentDto } = oeStudentInfo;
            const options = {
                action: "oeSchoolsByStudent",
                key: studentDto.key,
                params: {
                    enrollmentRound: oeActiveDistrictWindow.enrollmentRound
                },
                schoolYearKey: oeActiveDistrictWindow.schoolYearKey,
                token
            };
            setLoader(true);
            OpenEnrollmentDao(options)
                .then((response) => {
                    if (response) {
                        const { errors, payload } = response.data;
                        if (payload) {
                            setFeeders(payload);
                            updateSchoolChoiceDetailDtos(payload);
                        } else if (errors) {
                            let errorMessage = toastRegionError;
                            if (errors && errors.length > 0) {
                                errorMessage = `${errorMessage}:\n${errors.join("\n")}`;
                            }
                            toast.error(`${errorMessage}`, {
                                autoClose: false,
                                closeOnClick: true,
                                style: { width: "100%", whiteSpace: "break-spaces" }
                            });
                        }
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    }, [
        feeders,
        feederFlag,
        oeActiveDistrictWindow,
        oeStudentInfo,
        toastRegionError,
        token,
        updateSchoolChoiceDetailDtos
    ]);

    /**
     * Determine content display according to oeWindows
     */
    useEffect(() => {
        if (adwCalled && oeWindows && oeStudentInfo) {
            const {
                isParentOfferAllowed,
                isParentRequestAllowed,
                isParentViewBetweenRounds,
                isRound1,
                isSchoolFinalizeAllowed,
                isSchoolOfferAllowed,
                parentOffer,
                parentRequest,
                parentRequestRound2,
                schoolFinalize
            } = oeWindows;
            if (isParentRequestAllowed) {
                if (isRound1) {
                    setParentRequest(parentRequest);
                } else {
                    setParentRequest(parentRequestRound2);
                }
            } else if (isSchoolOfferAllowed && isRound1) {
                setNextParentOffer(parentOffer);
            } else if (isParentOfferAllowed && formState.schoolChoiceDetailDtos?.length > 0) {
                setParentOffer(parentOffer);
                setNextSteps(nextSteps1);
            } else if (isSchoolFinalizeAllowed && formState.schoolChoiceDetailDtos?.length > 0) {
                setSchoolFinalize(schoolFinalize);
                setNextSteps(nextSteps2);
            } else if (
                !isParentRequestAllowed &&
                !isParentOfferAllowed &&
                !isSchoolOfferAllowed &&
                !isSchoolFinalizeAllowed
            ) {
                if (isParentViewBetweenRounds && formState.schoolChoiceDetailDtos?.length > 0) {
                    setSchoolFinalize(schoolFinalize);
                    setNextSteps(nextSteps2);
                    setOutOfBounds(false);
                    setSorryTitle("");
                    setSorryMessage("");
                    setOpen("false");
                } else {
                    setOutOfBounds(true);
                    setSorryTitle(dialogClosedTitle);
                    setSorryMessage(dialogClosedBody);
                    setOpen("out-of-bounds");
                }
            }
        }
    }, [
        adwCalled,
        dialogClosedBody,
        dialogClosedTitle,
        formState.schoolChoiceDetailDtos,
        nextSteps1,
        nextSteps2,
        oeStudentInfo,
        oeWindows
    ]);

    /**
     * Populate employee info if user has any
     */
    useEffect(() => {
        if (oeGuardian) {
            const { employeeNumber, employeeUsername } = oeGuardian;
            const tmpFormState = formState;
            if (
                employeeNumber.length > 0 &&
                employeeUsername?.length > 0 &&
                (tmpFormState.employeeNumber?.length < 1 || tmpFormState?.employeeUsername?.length < 1)
            ) {
                tmpFormState.employeeNumber = employeeNumber;
                tmpFormState.employeeUsername = employeeUsername;
            }
            formDispatch({
                type: "reset",
                payload: { ...tmpFormState }
            });
        }
        /* eslint-disable-next-line */
    }, [feeders, formDispatch, oeGuardian]);

    useEffect(() => {
        if (!parentOffer) {
            setIsMutable(false);
        } else {
            setIsMutable(true);
        }
    }, [isMutable, parentOffer]);

    useEffect(() => {
        if (adwCalled && oeWindows && oeStudentInfo) {
            const { isParentRequestAllowed, isParentRequestRound2Allowed, isRound2 } = oeWindows;
            if (isRound2) {
                if (isParentRequestAllowed || isParentRequestRound2Allowed) {
                    if (formState.schoolChoiceDetailDtos && formState.schoolChoiceDetailDtos?.length > 0) {
                        const tmpFormState = formState;
                        const { schoolChoiceDetailDtos } = tmpFormState;
                        const enrolled = schoolChoiceDetailDtos.filter((obj) => obj.status === "ENROLLED");
                        const unenrolled = schoolChoiceDetailDtos.filter((obj) => obj.status === "CHANGED_ENROLLMENT");
                        const theRest = schoolChoiceDetailDtos.filter(
                            (obj) => obj.status !== "ENROLLED" && obj.status !== "CHANGED_ENROLLMENT"
                        );
                        const tmpSort = [];
                        if (enrolled && enrolled?.length > 0) {
                            tmpSort.push(enrolled[0]);
                        }
                        if (unenrolled && unenrolled?.length > 0) {
                            tmpSort.push(unenrolled[0]);
                        }
                        if (theRest && theRest?.length > 0) {
                            theRest.sort((a, b) => {
                                return parseInt(a.priority, 10) > parseInt(b.priority, 10) ? 1 : -1;
                            });
                        }
                        const combine = tmpSort.concat(theRest) || [];
                        tmpFormState.schoolChoiceDetailDtos = combine;
                        formDispatch({
                            type: "reset",
                            payload: { ...tmpFormState }
                        });
                    }
                }
            }
        }
        /* eslint-disable-next-line */
    }, [adwCalled, oeWindows, oeStudentInfo]);

    /**
     * If not Parent Request, and there are no school choices, shut it down
     */
    useEffect(() => {
        if (adwCalled && oeStudentInfo && oeWindows && formState.schoolChoiceDetailDtos) {
            const {
                isParentOfferAllowed,
                isParentRequestAllowed,
                isParentViewBetweenRounds,
                isParentRequestRound2Allowed,
                isSchoolOfferAllowed
            } = oeWindows;
            if (
                !(
                    isParentRequestAllowed ||
                    isParentRequestRound2Allowed ||
                    (formState.schoolChoiceDetailDtos?.length > 0 &&
                        (isParentOfferAllowed || isSchoolOfferAllowed || isParentViewBetweenRounds))
                )
            ) {
                setOutOfBounds(true);
                setSorryTitle(dialogClosedTitle);
                setSorryMessage(dialogClosedBody);
                setOpen("out-of-bounds");
            }
        }
    }, [adwCalled, dialogClosedBody, dialogClosedTitle, formState.schoolChoiceDetailDtos, oeStudentInfo, oeWindows]);

    return (
        <RbA allowedRoles={allowedRolesArray} redirect="/notFound">
            <ToastContainer style={{ width: "50%" }} />
            <Header />
            <Toolbar />
            <BreadCrumb label={getBreadCrumbLabel()} />
            <div className="school-choice-page">
                <div className="gutter-90">
                    <div className="employee-id-container">
                        <div>{oeStudentInfo && <StudentInfoBlock studentInfo={oeStudentInfo} />}</div>
                        {showEmployeeButton() && (
                            <ActionButton
                                className="action-button-reg employee-id-button"
                                label="DCSD Employee Enter ID"
                                onClick={() => {
                                    setOpen("employee-id");
                                }}
                            />
                        )}
                    </div>
                </div>
                {!outOfBounds && feeders && reasons && parentRequest && (
                    <>
                        <FeederBlock
                            currentFeeder={oeStudentInfo?.feederRegion}
                            feeders={feeders}
                            formDispatch={formDispatch}
                            formState={formState}
                            oeStudentInfo={oeStudentInfo}
                            setSelectedSchoolTab={setSelectedSchoolTab}
                        />
                        {formState.schoolChoiceDetailDtos.length > 0 && oeStudentInfo && (
                            <>
                                <hr className="hr-page" />
                                <SelectedSchoolsBlock
                                    feeders={feeders}
                                    formDispatch={formDispatch}
                                    formState={formState}
                                    oeStudentInfo={oeStudentInfo}
                                    reasons={reasons}
                                    selectedSchoolTab={selectedSchoolTab}
                                    setSelectedSchoolTab={setSelectedSchoolTab}
                                />
                            </>
                        )}
                        <SaveSchoolChoices
                            formState={formState}
                            handleSubmit={handleSubmit}
                            loader={loader}
                            open={open}
                            reasons={reasons}
                            setOpen={setOpen}
                            studentDto={oeStudentInfo.studentDto}
                        />
                        {oeStudentInfo && (
                            <>
                                <DcsdDialog
                                    actions={getDeleteActions()}
                                    ariaLabel="Delete Open Enrollment Confirmation Dialog"
                                    hasCloseX
                                    id="delete"
                                    onHide={() => {
                                        setOpen("false");
                                    }}
                                    open={open}
                                    title={oeDialogDeleteTitle}
                                >
                                    <p>
                                        {oeDialogDeleteBody1}{" "}
                                        <span>
                                            {oeStudentInfo?.studentDto.firstName} {oeStudentInfo?.studentDto.lastName}
                                        </span>
                                        . {oeDialogDeleteBody2}
                                    </p>
                                    {loader && (
                                        <div className="dialog-loader">
                                            {dialogProcessing} <LoadingSvg />
                                        </div>
                                    )}
                                </DcsdDialog>
                                <DcsdDialog
                                    actions={getEmployeeIdActions()}
                                    ariaLabel="Enter Employee ID Dialog"
                                    hasCloseX
                                    id="employee-id"
                                    onHide={() => {
                                        setOpen("false");
                                    }}
                                    open={open}
                                    title={getEmployeeIdTitle()}
                                >
                                    {employeeDelete ? (
                                        <div>
                                            <p>
                                                Please confirm you would like to delete the DCSD Employee Username{" "}
                                                <u>{formState.employeeUsername}</u> and DCSD Employee ID{" "}
                                                <u>{formState.employeeNumber}</u> from this Open Enrollment Request.
                                                This action will remove the corresponding priority points that may have
                                                been associated to your Open Enrollment school choices.
                                            </p>
                                            {loader && (
                                                <div className="dialog-loader">
                                                    {dialogProcessing} <LoadingSvg />
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            <div>
                                                <u>
                                                    Please enter your DCSD <strong>Employee Username</strong> and{" "}
                                                    <strong>Employee ID</strong>
                                                </u>{" "}
                                                to receive priority points. Your DCSD Employee ID should be entered{" "}
                                                <strong>WITHOUT</strong> leading zeros so the system can accurately
                                                validate. In order for the system to automatically assign priority
                                                points for the Open Enrollment request, the DCSD employee must have
                                                accessed and logged in to the{" "}
                                                <a href={EMPLOYEE_DASHBOARD} rel="noreferrer" target="_blank">
                                                    Employee Dashboard
                                                </a>{" "}
                                                at least one time.
                                            </div>
                                            <div>
                                                <form className="employee-id-form" id="employee-id-form">
                                                    <label>
                                                        Employee Username *
                                                        <input
                                                            id="employee-username"
                                                            name="employeeUsername"
                                                            onChange={handleChangeEmployee}
                                                            required
                                                            type="text"
                                                            value={formState.employeeUsername}
                                                        />
                                                        <p className="input-subtext">
                                                            Please enter the DCSD employee username associated with
                                                            Workday / DCSD Single-Sign-On
                                                        </p>
                                                    </label>
                                                    <label>
                                                        Employee ID *
                                                        <input
                                                            id="employee-id"
                                                            name="employeeNumber"
                                                            onChange={handleChangeEmployee}
                                                            required
                                                            type="text"
                                                            value={formState.employeeNumber}
                                                        />
                                                        <p className="input-subtext">
                                                            Please enter the employee ID associated with the username
                                                            you entered above
                                                        </p>
                                                    </label>
                                                </form>
                                            </div>
                                            {loader && (
                                                <div className="dialog-loader">
                                                    {dialogProcessing} <LoadingSvg />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </DcsdDialog>
                            </>
                        )}
                    </>
                )}
                {showNotice() && (
                    <>
                        {!schoolFinalize && (
                            <div className="gutter-90">
                                <GoldenRodBox
                                    includeHr
                                    label={importantParentInfo}
                                    message={getParentMessage()}
                                    school={false}
                                />
                            </div>
                        )}
                        {nextSteps && (
                            <div className="gutter-90">
                                <GoldenRodBox
                                    includeHr
                                    label={nextSteps0}
                                    message={
                                        <p>
                                            {nextSteps}
                                            <br />
                                            {nextStepsPost}
                                        </p>
                                    }
                                    school={false}
                                />
                            </div>
                        )}
                        <SelectedSchools
                            formDispatch={formDispatch}
                            formState={formState}
                            handleSubmit={handleSubmit}
                            isMutable={isMutable}
                            oeStudentInfo={oeStudentInfo}
                            parentOffer={parentOffer}
                            schoolFinalize={schoolFinalize}
                        />
                        {parentOffer ? (
                            <SaveAcceptedOffer
                                formDispatch={formDispatch}
                                formState={formState}
                                handleSubmit={handleSubmit}
                                isMutable={isMutable}
                                loader={loader}
                            />
                        ) : (
                            <BackOnly />
                        )}
                    </>
                )}
                {loader && <LoadingSvg />}
                <WindowAndPermissionDialog
                    id="out-of-bounds"
                    open={open}
                    setOpen={setOpen}
                    sorryTitle={sorryTitle}
                    sorryMessage={sorryMessage}
                />
            </div>
        </RbA>
    );
};

export default SchoolChoice;
